/**
 * Resideo carousel — initializes any root with [data-rs-carousel].
 * Expects: .slider-container, .slide, optional .slider-start-pause, optional .slider-dots
 * Re-initializes on theme editor section/block updates (shopify:section:load, block:select).
 */
(function () {
  /** @type {WeakMap<Element, { destroy: () => void }>} */
  const instances = new WeakMap();

  function teardownRsCarousel(root) {
    const inst = instances.get(root);
    if (inst) {
      inst.destroy();
      instances.delete(root);
    }
  }

  function initRsCarousel(root) {
    if (!root) return;

    teardownRsCarousel(root);

    const sliderContainer = root.querySelector('.slider-container');
    const slides = root.querySelectorAll('.slide');
    const startPauseButton = root.querySelector('.slider-start-pause');
    const dotsContainer = root.querySelector('.slider-dots');

    if (!sliderContainer || slides.length === 0) return;

    let currentSlide = 0;
    let autoSlideInterval = null;
    let containerWidth = sliderContainer.offsetWidth;
    let isPlaying = true;
    const parsedInterval = parseInt(root.dataset.autoplayInterval, 10);
    const intervalMs = Number.isFinite(parsedInterval) ? Math.max(parsedInterval, 2000) : 8000;
    const designMode = typeof Shopify !== 'undefined' && Shopify.designMode;

    /** @type {HTMLDivElement | null} */
    let slideAnnouncerEl = null;

    function enrichSlidesAccessibility() {
      slides.forEach((slide, index) => {
        if (!slide.hasAttribute('role')) {
          slide.setAttribute('role', 'group');
        }
        if (!slide.hasAttribute('aria-roledescription')) {
          slide.setAttribute('aria-roledescription', 'slide');
        }
        if (!slide.hasAttribute('aria-label')) {
          slide.setAttribute('aria-label', `Slide ${index + 1} of ${slides.length}`);
        }
      });
    }

    function ensureSlideAnnouncer() {
      if (slideAnnouncerEl?.isConnected) return slideAnnouncerEl;
      slideAnnouncerEl = document.createElement('div');
      slideAnnouncerEl.className = 'visually-hidden';
      slideAnnouncerEl.setAttribute('role', 'status');
      slideAnnouncerEl.setAttribute('aria-atomic', 'true');
      root.insertAdjacentElement('afterbegin', slideAnnouncerEl);
      return slideAnnouncerEl;
    }

    function announceCurrentSlide() {
      if (slides.length <= 1) return;
      const slide = slides[currentSlide];
      const slideLabel = slide.getAttribute('aria-label')?.trim();
      const text =
        slideLabel && slideLabel.length > 0 ? slideLabel : `Slide ${currentSlide + 1} of ${slides.length}`;
      const regionLabel = root.getAttribute('aria-label')?.trim();
      const msg = regionLabel ? `${regionLabel}. ${text}` : text;
      const el = ensureSlideAnnouncer();
      el.textContent = '';
      requestAnimationFrame(() => {
        el.textContent = msg;
      });
    }

    /**
     * @param {{ announce?: boolean }} [options]
     */
    function updateSlider(options) {
      const announce = Boolean(options && options.announce);
      containerWidth = sliderContainer.offsetWidth;
      slides.forEach((slide, index) => {
        slide.classList.remove('active');
        const isVisible = index === currentSlide;
        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        if (!designMode && slides.length > 1) {
          if (isVisible) {
            slide.removeAttribute('inert');
          } else {
            slide.setAttribute('inert', '');
          }
        }
        if (isVisible) {
          slide.classList.add('active');
          slide.style.transform = 'translateX(0)';
        } else if (index < currentSlide) {
          slide.style.transform = `translateX(-${containerWidth}px)`;
        } else {
          slide.style.transform = `translateX(${containerWidth}px)`;
        }
      });
      updateDots();
      if (announce) {
        announceCurrentSlide();
      }
    }

    function updateDots() {
      if (!dotsContainer) return;
      const dots = root.querySelectorAll('.slider-dot');
      const total = slides.length;
      dots.forEach((dot, index) => {
        const isActive = index === currentSlide;
        dot.classList.toggle('active', isActive);
        dot.setAttribute('tabindex', '0');
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
        dot.setAttribute('aria-label', `Slide ${index + 1} of ${total}`);
      });
    }

    function createDots() {
      if (!dotsContainer || slides.length === 0) return;
      dotsContainer.innerHTML = '';
      slides.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.classList.add('slider-dot');
        dot.setAttribute('tabindex', '0');
        dot.addEventListener('click', () => {
          currentSlide = index;
          updateSlider({ announce: true });
          dot.focus();
        });
        dotsContainer.appendChild(dot);
      });
      updateDots();
    }

    /** Arrow/Home/End change slide while focus is on a dot (optional; all dots are also in Tab order). */
    function onDotsKeydown(event) {
      if (!(event.target instanceof HTMLElement) || !event.target.classList.contains('slider-dot')) {
        return;
      }
      if (slides.length <= 1) return;

      const key = event.key;
      let nextIndex = null;

      if (key === 'ArrowRight') {
        nextIndex = currentSlide < slides.length - 1 ? currentSlide + 1 : 0;
      } else if (key === 'ArrowLeft') {
        nextIndex = currentSlide > 0 ? currentSlide - 1 : slides.length - 1;
      } else if (key === 'Home') {
        nextIndex = 0;
      } else if (key === 'End') {
        nextIndex = slides.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      if (nextIndex === currentSlide) return;

      currentSlide = nextIndex;
      updateSlider({ announce: true });
      const dots = root.querySelectorAll('.slider-dot');
      dots[nextIndex]?.focus();
    }

    function syncPlayPauseLabel() {
      if (!startPauseButton) return;
      const playLabel = startPauseButton.dataset.playLabel;
      const pauseLabel = startPauseButton.dataset.pauseLabel;
      if (!playLabel && !pauseLabel) return;
      if (startPauseButton.classList.contains('pause') && pauseLabel) {
        startPauseButton.setAttribute('aria-label', pauseLabel);
      } else if (startPauseButton.classList.contains('play') && playLabel) {
        startPauseButton.setAttribute('aria-label', playLabel);
      }
    }

    function startAutoSlide() {
      if (autoSlideInterval || !isPlaying || slides.length <= 1) return;
      autoSlideInterval = setInterval(() => {
        currentSlide = currentSlide < slides.length - 1 ? currentSlide + 1 : 0;
        updateSlider();
      }, intervalMs);
      if (startPauseButton) {
        startPauseButton.classList.remove('play');
        startPauseButton.classList.add('pause');
        syncPlayPauseLabel();
      }
    }

    function pauseAutoSlide() {
      clearInterval(autoSlideInterval);
      autoSlideInterval = null;
      if (startPauseButton) {
        startPauseButton.classList.remove('pause');
        startPauseButton.classList.add('play');
        syncPlayPauseLabel();
      }
      isPlaying = false;
    }

    function onResize() {
      updateSlider();
    }

    if (startPauseButton) {
      startPauseButton.addEventListener('click', () => {
        if (autoSlideInterval) {
          pauseAutoSlide();
        } else {
          isPlaying = true;
          startAutoSlide();
        }
      });
    }

    window.addEventListener('resize', onResize);

    enrichSlidesAccessibility();

    if (slides.length <= 1) {
      currentSlide = 0;
      updateSlider();
      instances.set(root, {
        destroy() {
          slideAnnouncerEl?.remove();
          slideAnnouncerEl = null;
          window.removeEventListener('resize', onResize);
        },
      });
      return;
    }

    createDots();
    if (dotsContainer) {
      dotsContainer.addEventListener('keydown', onDotsKeydown);
    }
    updateSlider();
    startAutoSlide();

    instances.set(root, {
      destroy() {
        clearInterval(autoSlideInterval);
        autoSlideInterval = null;
        slideAnnouncerEl?.remove();
        slideAnnouncerEl = null;
        window.removeEventListener('resize', onResize);
        dotsContainer?.removeEventListener('keydown', onDotsKeydown);
      },
    });
  }

  function initCarouselsIn(root) {
    if (!root || !(root instanceof Element)) return;
    root.querySelectorAll('[data-rs-carousel]').forEach(initRsCarousel);
  }

  function teardownCarouselsIn(root) {
    if (!root || !(root instanceof Element)) return;
    root.querySelectorAll('[data-rs-carousel]').forEach(teardownRsCarousel);
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-rs-carousel]').forEach(initRsCarousel);
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) {
      initCarouselsIn(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof HTMLElement) {
      teardownCarouselsIn(event.target);
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    if (event.target instanceof HTMLElement) {
      const section = event.target.closest('.shopify-section');
      if (section) {
        initCarouselsIn(section);
      }
    }
  });
})();
